import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { ClipboardPlus, ListChecks, BadgeCheck, BarChart3, Loader2, BookOpen, PlayCircle } from "lucide-react";

export default function PromoterOverview() {
  const [me, setMe] = useState(null);
  const [perf, setPerf] = useState(null);
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/promoter/me"),
      api.get("/promoter/me/performance"),
      api.get("/promoter/tasks?status=assigned"),
    ]).then(([a, b, c]) => {
      setMe(a.data);
      setPerf(b.data);
      setTasks(c.data.items || []);
    }).catch(() => {});
  }, []);
  if (!me) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div data-testid="promoter-overview-page">
      <h1 className="font-display text-2xl font-semibold">Hi, {me.promoter?.name?.split(" ")[0] || "Promoter"}</h1>
      <p className="text-sm text-slate-600 mt-1">
        Tap a tile to get started. Submit a visit log after every business you talk to, even if they say no.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <ActionTile to="/promoter/guide" icon={BookOpen} title="Sales Guide"
                      sub="Pitch · FAQs · objections · important rules" testId="promoter-tile-guide" />
        <ActionTile to="/promoter/demos" icon={PlayCircle} title="Interactive Demos"
                      sub="Show real ConfirmPH workflows using safe fake scenarios" testId="promoter-tile-demos" />
        <ActionTile to="/promoter/new-log" icon={ClipboardPlus} title="Log a new visit"
                      sub="Quick form: under 60 seconds" testId="promoter-tile-new-log" />
        <ActionTile to="/promoter/tasks" icon={BadgeCheck} title="My assigned visits"
                      sub={`${tasks.length} open`} testId="promoter-tile-tasks" />
        <ActionTile to="/promoter/logs" icon={ListChecks} title="My submitted logs"
                      sub={perf ? `${perf.visited} total` : ""} testId="promoter-tile-logs" />
      </div>
      {perf && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="promoter-perf-card">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Your performance</h2>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
            <PerfTile testId="perf-visited" label="Visited" value={perf.visited} />
            <PerfTile testId="perf-interested" label="Interested" value={perf.interested} accent="text-emerald-700" />
            <PerfTile testId="perf-follow-up" label="Follow-up needed" value={perf.follow_up_needed} accent="text-amber-700" />
            <PerfTile testId="perf-signed-up" label="Signed up" value={perf.signed_up} accent="text-[#1B4332] font-bold" />
            <PerfTile testId="perf-not-interested" label="Not interested" value={perf.not_interested} accent="text-rose-700" />
            <PerfTile testId="perf-tasks-open" label="Open tasks" value={perf.tasks_assigned - perf.tasks_completed} />
            <PerfTile testId="perf-tasks-done" label="Tasks completed" value={perf.tasks_completed} />
            <PerfTile testId="perf-tasks-overdue" label="Tasks overdue" value={perf.tasks_overdue} accent="text-rose-700" />
          </div>
        </div>
      )}
    </div>
  );
}

function ActionTile({ to, icon: Icon, title, sub, testId }) {
  return (
    <Link to={to} data-testid={testId}
           className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#1B4332]/40 transition group">
      <div className="w-10 h-10 rounded-lg bg-[#1B4332]/10 text-[#1B4332] flex items-center justify-center group-hover:bg-[#1B4332] group-hover:text-white transition">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-display text-lg font-semibold mt-3">{title}</div>
      <div className="text-xs text-slate-600 mt-1">{sub}</div>
    </Link>
  );
}

function PerfTile({ label, value, accent = "", testId }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl ${accent || "text-slate-900"} font-display mt-0.5`}>{value ?? 0}</div>
    </div>
  );
}
